import { ApiError } from "@/lib/api/error";
import type { SearchFiltersSchema, TalentPoolResponse } from "@/lib/api/model";

/** `shared.schema.pools.STANDARD_POOL_ID`: Arbi's standard global pool. */
export const STANDARD_POOL_ID = "1b1d6974-918a-578c-8cd2-a17092a1db18";
/** `MAX_POOLS_PER_SEARCH` on the backend. */
export const MAX_POOLS_PER_SEARCH = 10;

/**
 * Mirror of the backend's `FILTER_CATEGORIES`
 * (`app/services/search/filter_categories.py`): the unit a talent pool can
 * turn off. Keys outside every category (pool membership, network, viewer
 * exclusions) work on every pool.
 */
export const FILTER_CATEGORIES = {
  job: ["job_titles", "past_job_titles", "jt_filter_type", "job_title_levels", "job_title_roles"],
  locations: ["locations", "past_locations", "distance"],
  company: [
    "companies",
    "company_locations",
    "company_sizes",
    "company_tags",
    "company_tag_filter_type",
    "company_founded_after",
  ],
  industry: ["industries", "industry_filter_type"],
  funding: [
    "company_funding_stages",
    "funding_filter_type",
    "investors",
    "investor_filter_type",
    "min_funding",
    "max_funding",
    "funding_scope",
    "revenue_ranges",
  ],
  skills: ["skills"],
  education: [
    "universities",
    "university_locations",
    "min_degree",
    "majors",
    "grad_year_start",
    "grad_year_end",
    "education_constraints",
  ],
  languages: ["languages"],
  certifications: ["certification_names", "certification_vendors", "certification_status"],
  federal: [
    "occupational_series",
    "gs_grade_min",
    "gs_grade_max",
    "gs_include_equivalents",
    "clearance_levels",
    "clearance_strict",
    "clearance_polygraphs",
  ],
  highlights: ["highlight_types", "power_filters"],
  general: [
    "min_experience_years",
    "max_experience_years",
    "time_current_role",
    "time_current_company",
    "average_tenure_min_months",
    "likely_to_switch",
    "experience_constraints",
  ],
  name: ["full_names"],
  text: ["boolean_expression", "full_text_search", "keywords"],
} as const satisfies Record<string, readonly (keyof SearchFiltersSchema)[]>;

export type FilterCategory = keyof typeof FILTER_CATEGORIES;
export const CATEGORY_IDS = Object.keys(FILTER_CATEGORIES) as FilterCategory[];

export const CATEGORY_LABELS: Record<FilterCategory, string> = {
  job: "Job",
  locations: "Locations",
  company: "Company",
  industry: "Industry",
  funding: "Funding",
  skills: "Skills",
  education: "Education",
  languages: "Languages",
  certifications: "Certifications",
  federal: "Federal",
  highlights: "Highlights",
  general: "Experience",
  name: "Name",
  text: "Boolean & keywords",
};

export function categoryLabel(id: string): string {
  return CATEGORY_LABELS[id as FilterCategory] ?? id;
}

/** Filter-panel sections whose inputs read categories other than their own key. */
const SECTION_CATEGORIES: Record<string, readonly FilterCategory[]> = {
  booleanName: ["name", "text"],
};

/** Categories a filter-panel section reads (none for always-available sections). */
export function sectionCategories(sectionKey: string): readonly FilterCategory[] {
  if (sectionKey in SECTION_CATEGORIES) return SECTION_CATEGORIES[sectionKey];
  return sectionKey in FILTER_CATEGORIES ? [sectionKey as FilterCategory] : [];
}

/** Sorted, de-duplicated pool ids (the backend's `normalize_pool_ids`). */
export function normalizePoolIds(ids: readonly string[]): string[] {
  return [...new Set(ids)].sort();
}

/**
 * `ids` cut down to a scope the backend accepts: exactly one Arbi (global)
 * pool, or 1..MAX_POOLS_PER_SEARCH of the org's own pools, never both. Own
 * pools win over Arbi pools; of several Arbi pools the standard one (else the
 * lowest id) stays, matching the migration that normalized stored searches.
 */
export function searchScopePoolIds(
  pools: readonly TalentPoolResponse[],
  ids: readonly string[],
): string[] {
  const tenant = new Set(pools.filter((p) => p.kind === "tenant").map((p) => p.id));
  const unique = normalizePoolIds(ids);
  const tenants = unique.filter((id) => tenant.has(id));
  if (tenants.length) return tenants.slice(0, MAX_POOLS_PER_SEARCH);
  if (unique.length <= 1) return unique;
  return [unique.includes(STANDARD_POOL_ID) ? STANDARD_POOL_ID : unique[0]];
}

/** Pools a new search starts with: those flagged `default_search`, as a valid scope. */
export function defaultSearchPoolIds(pools: readonly TalentPoolResponse[]): string[] {
  return searchScopePoolIds(
    pools,
    pools.filter((p) => p.default_search).map((p) => p.id),
  );
}

/** Union of the selected pools' disabled categories, with the pools disabling each. */
export function unavailableCategories(
  pools: readonly TalentPoolResponse[],
  selectedIds: readonly string[],
): Map<string, TalentPoolResponse[]> {
  const selected = new Set(selectedIds);
  const out = new Map<string, TalentPoolResponse[]>();
  for (const pool of pools) {
    if (!selected.has(pool.id)) continue;
    for (const category of pool.disabled_filter_categories ?? []) {
      out.set(category, [...(out.get(category) ?? []), pool]);
    }
  }
  return out;
}

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * `filters` with every key of `categories` removed, plus the categories that
 * actually held a value (what the user should be told was cleared).
 */
export function clearCategories(
  filters: SearchFiltersSchema,
  categories: Iterable<string>,
): { filters: SearchFiltersSchema; cleared: string[] } {
  const next: Record<string, unknown> = { ...filters };
  const cleared: string[] = [];
  for (const category of categories) {
    const keys: readonly string[] = FILTER_CATEGORIES[category as FilterCategory] ?? [];
    let had = false;
    for (const key of keys) {
      if (hasValue(next[key])) had = true;
      delete next[key];
    }
    if (had) cleared.push(category);
  }
  return { filters: next as SearchFiltersSchema, cleared };
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * User-facing text for a 422 `FILTER_NOT_AVAILABLE` (`{categories, pools}`),
 * or null for any other error.
 */
export function filterNotAvailableMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  if (err.code !== "FILTER_NOT_AVAILABLE" && err.detail.code !== "FILTER_NOT_AVAILABLE") {
    return null;
  }
  const categories = Array.isArray(err.detail.categories)
    ? (err.detail.categories as string[]).map(categoryLabel)
    : [];
  const pools = Array.isArray(err.detail.pools)
    ? (err.detail.pools as { name?: string }[]).flatMap((p) => (p.name ? [p.name] : []))
    : [];
  const what = categories.length ? `${joinNames(categories)} filters` : "Some filters";
  const where = pools.length ? joinNames(pools) : "the selected talent pools";
  return `${what} aren't available in ${where}. Remove them or change the pools, then run again.`;
}
